import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { subscribeLeads } from '../data/listeners/leads';
import { subscribeConfig } from '../data/listeners/config';
import { addNote } from '../data/commands/noteCommands';
import { updateLead } from '../data/commands/leadCommands';
import { withDefaults, validateTouch, isVisitChannel } from '../data/proof';
import { compressImage, uploadProof } from '../lib/storage';
import { VISIT_CHANNELS, CADENCE_STEPS } from '../data/leadConstants';
import { advanceCadence } from '../data/cadence';
import { searchLeads, lastVisitLabel } from '../data/leadSearch';
import { useIsMobile } from '../hooks/useIsMobile';
import Icon from './Icon';

// Signature pad on a canvas (ported from mockup initSig/clearSig). `active`
// re-runs the effect when the canvas mounts/unmounts (it only renders for a
// physical visit) so listeners attach to the freshly-mounted canvas.
function useSignature(canvasRef, active) {
  useEffect(() => {
    if (!active) return undefined;
    const c = canvasRef.current; if (!c) return undefined;
    c.width = c.offsetWidth; c.height = 120;
    const x = c.getContext('2d'); x.strokeStyle = '#222227'; x.lineWidth = 2; x.lineCap = 'round';
    let down = false; let px = 0; let py = 0;
    const pos = (e) => { const r = c.getBoundingClientRect(); const t = e.touches ? e.touches[0] : e; return [t.clientX - r.left, t.clientY - r.top]; };
    const start = (e) => { down = true; [px, py] = pos(e); e.preventDefault(); };
    const move = (e) => { if (!down) return; const [a, b] = pos(e); x.beginPath(); x.moveTo(px, py); x.lineTo(a, b); x.stroke(); [px, py] = [a, b]; e.preventDefault(); };
    const end = () => { down = false; };
    c.addEventListener('mousedown', start); c.addEventListener('mousemove', move); window.addEventListener('mouseup', end);
    c.addEventListener('touchstart', start); c.addEventListener('touchmove', move); c.addEventListener('touchend', end);
    return () => {
      c.removeEventListener('mousedown', start); c.removeEventListener('mousemove', move); window.removeEventListener('mouseup', end);
      c.removeEventListener('touchstart', start); c.removeEventListener('touchmove', move); c.removeEventListener('touchend', end);
    };
  }, [canvasRef, active]);
}

export default function LogVisitModal() {
  const { visit, closeLogVisit } = useUI();
  const { tenantId, role, territoryIds, profile, authUser } = useAuth();
  const [leads, setLeads] = useState([]);
  const [config, setConfig] = useState(null);
  const [leadId, setLeadId] = useState('');
  const [query, setQuery] = useState(''); // BUG-019: searchable picker text
  const [listOpen, setListOpen] = useState(false);
  const [channel, setChannel] = useState('In-person');
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState(null); // File
  const [signedBy, setSignedBy] = useState('');
  const [nextStep, setNextStep] = useState('Email');
  const [nextDate, setNextDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const canvasRef = useRef(null);
  const sigDrawn = useRef(false);
  const open = visit != null;
  const isMobile = useIsMobile();
  const isVisit = isVisitChannel(channel);
  useSignature(canvasRef, open && isVisit);
  const c = withDefaults(config);
  const actor = { uid: authUser?.uid, name: profile?.name ?? profile?.email, role };

  useEffect(() => {
    if (!open) return undefined;
    setLeadId(visit.leadId ?? '');
    setQuery(''); setListOpen(false);
    setChannel('In-person'); setText(''); setPhoto(null); setSignedBy(''); setNextStep('Email'); setNextDate(''); setError('');
    sigDrawn.current = false;
    return undefined;
  }, [open, visit?.leadId]);

  // When opened from a specific lead (drawer), show its name in the picker once
  // the leads snapshot lands (async). Only fills the preset; never clobbers typing.
  useEffect(() => {
    if (!open || !visit?.leadId || leadId !== visit.leadId || query) return;
    const l = leads.find((x) => x.id === visit.leadId);
    if (l) setQuery(l.businessName ?? '');
  }, [open, visit?.leadId, leadId, query, leads]);

  useEffect(() => { if (open && tenantId) return subscribeLeads({ tenantId, role, territoryIds }, setLeads); return undefined; }, [open, tenantId, role, territoryIds]);
  useEffect(() => { if (open && tenantId) return subscribeConfig(tenantId, setConfig); return undefined; }, [open, tenantId]);

  if (!open) return null;

  function clearSig() {
    const cv = canvasRef.current; if (cv) cv.getContext('2d').clearRect(0, 0, cv.width, cv.height);
    sigDrawn.current = false;
  }
  function markSig() { sigDrawn.current = true; }

  function signatureBlob() {
    return new Promise((resolve) => {
      const cv = canvasRef.current;
      if (!cv || !sigDrawn.current) { resolve(null); return; }
      cv.toBlob((b) => resolve(b), 'image/png');
    });
  }

  async function save() {
    // Signature only counts as proof on a physical visit. On a remote channel
    // it's hidden, so proof falls to a screenshot or written notes.
    const hasSignature = isVisit && sigDrawn.current;
    const gate = validateTouch(c, {
      channel, hasPhoto: Boolean(photo), hasSignature, hasText: text.trim().length > 0,
      nextStep: text || nextStep,
    });
    if (!gate.ok) { setError(gate.errors.join(' ')); return; }
    if (!leadId) { setError('Pick a customer / lead.'); return; }
    setSaving(true); setError('');
    try {
      const proofUrls = [];
      if (photo) proofUrls.push(await uploadProof(tenantId, leadId, await compressImage(photo), 'photo'));
      const sig = isVisit ? await signatureBlob() : null;
      if (sig) proofUrls.push(await uploadProof(tenantId, leadId, sig, 'signature'));
      const hasProof = Boolean(photo) || Boolean(sig) || (!isVisit && text.trim().length > 0);
      await addNote(leadId, {
        type: 'Action', code: channel, text: signedBy ? `${text} (signed by ${signedBy})` : text,
        hasProof, channel, proofUrls,
      }, actor);
      // A logged visit always stamps the touch time + advances the cadence one
      // step (guidance only). nextAction/date are optional overrides.
      const lead = leads.find((l) => l.id === leadId);
      const patch = {
        lastTouchAt: Date.now(),
        cadenceStep: advanceCadence(lead?.cadenceStep),
      };
      if (nextStep) patch.nextAction = nextStep;
      if (nextDate) patch.nextDate = new Date(nextDate).getTime();
      await updateLead(leadId, patch, actor);
      closeLogVisit();
    } catch (e) {
      setError(e.message || 'Could not save the touch.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={closeLogVisit}>
      <div className={`modal${isMobile ? ' sheet-full' : ''}`} onClick={(e) => e.stopPropagation()}>
        <header><Icon name="logvisit" size={18} /> Log Touchpoint</header>
        <div className="body">
          <div className="field"><label>Customer / lead</label>
            {/* BUG-019: searchable combobox (was a plain <select>, unwieldy at
                100+ leads). Each option shows the lead's last-visit date. */}
            <div className="combo">
              <input
                className="input"
                placeholder="Search customer / lead…"
                aria-label="Customer / lead"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setLeadId(''); setListOpen(true); }}
                onFocus={() => setListOpen(true)}
              />
              {listOpen && (
                <div className="combo-list">
                  {searchLeads(leads, query).length === 0 && (
                    <div className="combo-empty muted">No matching customer / lead</div>
                  )}
                  {searchLeads(leads, query).slice(0, 50).map((l) => (
                    <button
                      type="button"
                      key={l.id}
                      className={`combo-opt${l.id === leadId ? ' on' : ''}`}
                      onClick={() => { setLeadId(l.id); setQuery(l.businessName ?? ''); setListOpen(false); }}
                    >
                      <span className="combo-name">{l.businessName}</span>
                      <span className="combo-meta muted">Last visit: {lastVisitLabel(l.lastTouchAt)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="field"><label>Channel</label>
            <div className="seg" style={{ flexWrap: 'wrap' }}>
              {VISIT_CHANNELS.map((ch) => (
                <button key={ch} type="button" className={channel === ch ? 'on' : ''} onClick={() => setChannel(ch)}>{ch}</button>
              ))}
            </div>
          </div>
          <div className="field"><label>What happened? (free text)</label>
            <textarea className="input" style={{ minHeight: 64, width: '100%' }} placeholder="e.g. GM interested in weekly pickup; quoted ₱60/kg…" value={text} onChange={(e) => setText(e.target.value)} />
          </div>
          <div className="field">
            <label>Proof of touchpoint {c.proofPolicy === 'required' ? <span style={{ color: 'var(--presto-error)' }}>· required</span> : <span className="muted">· optional</span>}</label>
            {c.proofPolicy === 'required' && !isVisit && (
              <div className="muted" style={{ fontSize: 'var(--text-caption)', marginBottom: 6 }}>
                For {channel}, attach a screenshot or fill in “What happened?” above — a signature isn’t possible on this channel.
              </div>
            )}
            <div className="photo-drop">
              <Icon name="importf" size={20} /><br /><b>Add photo or screenshot</b><br />
              <span style={{ fontSize: 'var(--text-caption)' }}>Storefront, receipt, or an email/Viber screenshot. Auto-compressed before upload.</span><br />
              {/* No `capture` attr: the OS picker offers gallery/screenshots AND
                  camera, so existing photos and Viber/email screenshots upload
                  too (BUG-021). Forcing capture="environment" locked it to camera. */}
              <input type="file" accept="image/*" style={{ marginTop: 8 }} onChange={(e) => setPhoto(e.target.files?.[0] ?? null)} />
              {photo && <div className="muted" style={{ fontSize: 'var(--text-caption)', marginTop: 4 }}>{photo.name}</div>}
            </div>
          </div>
          {/* Signature only for a physical visit — you can't sign an SMS/email. */}
          {isVisit && (
            <>
              <div className="field"><label>Signed by (contact name)</label><input className="input" placeholder="e.g. J. Ramos — Front Office" value={signedBy} onChange={(e) => setSignedBy(e.target.value)} /></div>
              <div className="field"><label>Signature</label>
                <canvas id="sigpad" ref={canvasRef} onMouseDown={markSig} onTouchStart={markSig} />
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="lockline">Sign with finger / mouse</span>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={clearSig}>Clear</button>
                </div>
              </div>
            </>
          )}
          <div className="grid2">
            <div className="field"><label>Next step</label><select className="input" value={nextStep} onChange={(e) => setNextStep(e.target.value)}>{CADENCE_STEPS.map((s) => <option key={s}>{s}</option>)}</select></div>
            <div className="field"><label>Next date</label><input className="input" type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} /></div>
          </div>
          {error && <div className="form-error">{error}</div>}
        </div>
        <footer>
          <button className="btn btn-ghost" onClick={closeLogVisit}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save touch'}</button>
        </footer>
      </div>
    </div>
  );
}
